import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

function RealisticParentLogin({ onLogin, onBack }) {
  const [parentId, setParentId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/parent-login`, {
        parentId: parentId.trim(),
        password: password.trim()
      });
      
      if (response?.data?.success && response?.data?.user) {
        onLogin(response.data.user);
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = () => {
    setParentId('PARENT_DEMO_5A');
    setPassword('demo123');
  };

  return (
    <div className="parent-login-container">
      <div className="parent-login-form">
        <div className="header">
          <button className="back-btn" onClick={onBack}>←</button>
          <div className="header-content">
            <div className="logo">
              <div className="logo-text">R</div>
            </div>
            <h1 className="app-title">Rising 360</h1>
            <p className="app-subtitle">Parents Business Community</p>
          </div>
        </div>

        <div className="form-container">
          <div className="welcome-text">
            <h2 className="welcome-title">Welcome Back!</h2>
            <p className="welcome-subtitle">Connect with trusted parent businesses</p>
          </div>

          <div className="demo-info">
            <div className="demo-badge">Demo Mode</div>
            <h4>Try Parent Community</h4>
            <p>Experience our parent business network with demo access</p>
            <button type="button" className="demo-btn" onClick={handleDemoLogin}>
              🚀 Use Demo Login
            </button>
          </div>

          <div className="divider">
            <span>sign in with your account</span>
          </div>

          <form onSubmit={handleSubmit}>
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-group">
              <label className="form-label">Parent ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter your Parent ID"
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="password-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <button type="submit" className="login-btn" disabled={isLoading}>
              {isLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  Signing In...
                </div>
              ) : (
                'Sign In to Community'
              )}
            </button>
          </form>

          <div className="register-link">
            <p>New to the community? <span className="highlight">Join thousands of parents!</span></p>
          </div>

          <div className="features">
            <h3 className="features-title">Parent Business Network</h3>
            <div className="feature-item">
              <div className="feature-icon">🏪</div>
              <span>Discover trusted parent-owned businesses</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">💼</div>
              <span>Find flexible job opportunities</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🤝</div>
              <span>Build professional parent network</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📱</div>
              <span>Direct WhatsApp communication</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .parent-login-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #E3F2FD 0%, #FFFFFF 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .parent-login-form {
          max-width: 400px;
          width: 100%;
          background: white;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(30, 90, 168, 0.12);
          overflow: hidden;
          border: 1px solid rgba(30, 90, 168, 0.08);
        }

        .header {
          background: linear-gradient(135deg, #1E5AA8 0%, #2196F3 100%);
          padding: 32px 24px;
          color: white;
          position: relative;
        }

        .back-btn {
          position: absolute;
          top: 20px;
          left: 20px;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.2);
          color: white;
          font-size: 16px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .back-btn:hover {
          background: rgba(255,255,255,0.25);
        }

        .header-content {
          text-align: center;
        }

        .logo {
          width: 56px;
          height: 56px;
          background: white;
          border-radius: 12px;
          margin: 0 auto 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .logo-text {
          font-size: 24px;
          color: #1E5AA8;
          font-weight: 700;
        }

        .app-title {
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .app-subtitle {
          font-size: 14px;
          opacity: 0.9;
          font-weight: 400;
        }

        .form-container {
          padding: 32px 24px;
        }

        .welcome-text {
          text-align: center;
          margin-bottom: 24px;
        }

        .welcome-title {
          font-size: 24px;
          font-weight: 600;
          color: #1E5AA8;
          margin-bottom: 6px;
        }

        .welcome-subtitle {
          font-size: 14px;
          color: #64748B;
          font-weight: 400;
        }

        .demo-info {
          background: linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%);
          border: 1px solid #F57C00;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
          text-align: center;
        }

        .demo-badge {
          background: #F57C00;
          color: white;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 11px;
          font-weight: 600;
          display: inline-block;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .demo-info h4 {
          color: #E65100;
          font-size: 15px;
          margin-bottom: 6px;
          font-weight: 600;
        }

        .demo-info p {
          color: #E65100;
          font-size: 13px;
          margin-bottom: 12px;
          line-height: 1.4;
        }

        .demo-btn {
          background: linear-gradient(135deg, #F57C00 0%, #FF9800 100%);
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(245, 124, 0, 0.25);
        }

        .demo-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(245, 124, 0, 0.35);
        }

        .divider {
          text-align: center;
          margin: 20px 0;
          position: relative;
        }

        .divider::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: #E2E8F0;
        }

        .divider span {
          background: white;
          padding: 0 12px;
          color: #94A3B8;
          font-size: 13px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #334155;
          margin-bottom: 6px;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          font-size: 15px;
          background: #FAFBFC;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .form-input:focus {
          outline: none;
          border-color: #1E5AA8;
          background: white;
          box-shadow: 0 0 0 3px rgba(30, 90, 168, 0.1);
        }

        .password-container {
          position: relative;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          font-size: 16px;
          color: #64748B;
        }

        .login-btn {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #F57C00 0%, #FF9800 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 16px;
          box-shadow: 0 4px 12px rgba(245, 124, 0, 0.25);
        }

        .login-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(245, 124, 0, 0.35);
        }

        .login-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .loading-spinner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-message {
          color: #DC2626;
          background: #FEF2F2;
          border: 1px solid #FECACA;
          border-radius: 8px;
          padding: 10px 12px;
          margin-bottom: 16px;
          font-size: 14px;
        }

        .register-link {
          text-align: center;
          margin-bottom: 24px;
        }

        .register-link .highlight {
          color: #F57C00;
          font-weight: 600;
        }

        .features {
          background: #F8FAFC;
          padding: 20px;
          border-radius: 12px;
          border: 1px solid #E2E8F0;
        }

        .features-title {
          font-size: 16px;
          font-weight: 600;
          color: #1E5AA8;
          margin-bottom: 16px;
          text-align: center;
        }

        .feature-item {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
          font-size: 14px;
          color: #475569;
        }

        .feature-icon {
          width: 20px;
          height: 20px;
          margin-right: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
        }

        @media (max-width: 480px) {
          .parent-login-container {
            padding: 12px;
          }
          
          .form-container {
            padding: 24px 20px;
          }
          
          .header {
            padding: 24px 20px;
          }
        }
      `}</style>
    </div>
  );
}

export default RealisticParentLogin;